<template>
  <div class="company-settings">
    <!-- Photos -->
    <section class="settings-card">
      <div class="settings-card__header">
        <h3>{{ $t("settings.section_photos") }}</h3>
        <span class="settings-card__badge settings-card__badge--public">
          {{ $t("settings.badge_public") }}
        </span>
      </div>
      <p class="settings-card__hint">{{ $t("settings.photos_hint") }}</p>

      <div class="photos-grid">
        <PhotoUploader
          v-if="mainImage"
          @update-images="onImagesUpdate"
          @delete-image="onImageDelete"
          @remove-pending-upload="onRemovePendingUpload"
          :image="mainImage"
          primary
          :hint="$t('settings.photos_hint_short')"
          class="photos-grid__main"
        />
        <PhotoUploader
          v-else
          @update-images="onImagesUpdate"
          @remove-pending-upload="onRemovePendingUpload"
          primary
          :hint="$t('settings.photos_hint_short')"
          class="photos-grid__main"
        />

        <div class="photos-grid__thumbs">
          <template
            v-for="(item, index) in visibleCompanyImages"
          >
            <PhotoUploader
              class="photos-grid__thumb"
              @update-images="onImagesUpdate"
              @delete-image="onImageDelete"
              @remove-pending-upload="onRemovePendingUpload"
              :image="item"
              :hint="$t('settings.add_photo')"
              v-if="index <= 3 && index != 0"
            />
          </template>

          <PhotoUploader
            class="photos-grid__thumb"
            @update-images="onImagesUpdate"
            @remove-pending-upload="onRemovePendingUpload"
            :hint="$t('settings.add_photo')"
            v-if="companyImagesLenght <= 1"
          />
          <PhotoUploader
            class="photos-grid__thumb"
            @update-images="onImagesUpdate"
            @remove-pending-upload="onRemovePendingUpload"
            :hint="$t('settings.add_photo')"
            v-if="companyImagesLenght <= 2"
          />
          <PhotoUploader
            class="photos-grid__thumb"
            @update-images="onImagesUpdate"
            @remove-pending-upload="onRemovePendingUpload"
            :hint="$t('settings.add_photo')"
            v-if="companyImagesLenght <= 3"
          />
        </div>
      </div>
    </section>

    <DeleteDialog v-model="deleteImageDialog" @delete="confirmDeleteImage" />

    <!-- Basic info -->
    <section class="settings-card">
      <div class="settings-card__header">
        <h3>{{ $t("settings.section_basic_info") }}</h3>
        <span class="settings-card__badge settings-card__badge--public">
          {{ $t("settings.badge_public") }}
        </span>
      </div>

      <div class="settings-field">
        <label class="settings-field__label">{{
          $t("settings.bussiness_name")
        }}</label>
        <input
          v-model="businessName"
          type="text"
          class="business-name-field__input"
          :placeholder="$t('settings.bussiness_name')"
        />
      </div>

      <div class="company-settings__feedbacks">
        {{ companyStore.currentCompany?.rating }}/5

        <div class="feedbacks">
          <div class="feedbacks__star" v-for="c in possibleRating">
            <VIcon
              :icon="`mdi-star${companyStore.currentCompany && companyStore.currentCompany!.rating! < c ? '-outline' : ''}`"
            ></VIcon>
          </div>
        </div>

        <span>{{ $t("feedbacks") }}</span>
      </div>

      <div class="settings-field">
        <label class="settings-field__label">{{
          $t("settings.company_desc")
        }}</label>
        <textarea
          v-model="description"
          :placeholder="$t('settings.company_desc_placeholder')"
        ></textarea>
      </div>
    </section>

    <!-- Public booking page -->
    <section class="settings-card">
      <div class="settings-card__header">
        <h3>{{ $t("settings.public_booking") }}</h3>
        <span class="settings-card__badge settings-card__badge--public">
          {{ $t("settings.badge_public") }}
        </span>
      </div>

      <VSwitch
        v-model="isPublic"
        :label="$t('settings.public_booking_enable')"
        density="comfortable"
        hide-details
      />

      <div
        class="booking-link"
        v-if="isPublic && companyStore.currentCompany?.booking_url"
      >
        <VIcon icon="mdi-link-variant" size="16" />
        <a
          :href="companyStore.currentCompany.booking_url"
          target="_blank"
          class="booking-link__url"
        >
          {{ companyStore.currentCompany.booking_url }}
        </a>
        <button
          type="button"
          class="booking-link__copy"
          @click="copyBookingUrl"
          :aria-label="$t('settings.copy_link')"
        >
          <VIcon :icon="copied ? 'mdi-check' : 'mdi-content-copy'" size="16" />
        </button>
      </div>

      <template v-if="isPublic">
        <div class="settings-grid">
          <VAutocomplete
            v-model="timezone"
            :items="timezoneOptions"
            :label="$t('settings.timezone')"
            density="comfortable"
          />
          <VTextField
            v-model="phone"
            :label="$t('settings.phone')"
            density="comfortable"
          />
          <VTextField
            v-model="email"
            :label="$t('settings.public_email')"
            density="comfortable"
          />
        </div>

        <div class="settings-field">
          <label class="settings-field__label">{{
            $t("settings.theme_color")
          }}</label>
          <VMenu v-model="colorMenuOpen" :close-on-content-click="false">
            <template #activator="{ props: menuProps }">
              <button type="button" class="color-swatch" v-bind="menuProps">
                <span
                  class="color-swatch__dot"
                  :style="{ backgroundColor: themeColor }"
                ></span>
                <span class="color-swatch__hex">{{ themeColor }}</span>
              </button>
            </template>
            <VColorPicker
              v-model="themeColor"
              hide-canvas
              hide-inputs
              mode="hex"
            />
          </VMenu>
        </div>

        <div class="settings-field logo-field">
          <label class="settings-field__label" for="company-logo-input">
            {{ $t("settings.logo") }}
          </label>
          <div class="logo-field__row">
            <label
              for="company-logo-input"
              class="logo-field__preview"
              :class="{ 'logo-field__preview-empty': !logoPreviewUrl }"
            >
              <img v-if="logoPreviewUrl" :src="logoPreviewUrl" alt="" />
              <VIcon v-else icon="mdi-image-plus-outline" size="22" />
            </label>
            <div class="logo-field__meta">
              <label for="company-logo-input" class="logo-field__button">
                {{ logoPreviewUrl ? $t("change") : $t("settings.upload") }}
              </label>
              <span class="logo-field__hint">{{
                $t("settings.logo_hint")
              }}</span>
            </div>
            <input
              id="company-logo-input"
              type="file"
              accept="image/*"
              class="logo-field__input"
              @change="onLogoChange"
            />
          </div>
        </div>
      </template>
    </section>

    <!-- Booking rules -->
    <section class="settings-card" v-if="isPublic">
      <div class="settings-card__header">
        <h3>{{ $t("settings.section_booking_rules") }}</h3>
        <span class="settings-card__badge settings-card__badge--internal">
          {{ $t("settings.badge_internal") }}
        </span>
      </div>

      <div class="settings-grid">
        <VTextField
          v-model.number="minAdvanceHours"
          type="number"
          min="0"
          :suffix="$t('settings.hours_suffix')"
          :label="$t('settings.min_advance_hours')"
          :hint="$t('settings.min_advance_hours_hint')"
          persistent-hint
          density="comfortable"
        />
        <VTextField
          v-model.number="maxAdvanceDays"
          type="number"
          min="1"
          :suffix="$t('settings.days_suffix')"
          :label="$t('settings.max_advance_days')"
          :hint="$t('settings.max_advance_days_hint')"
          persistent-hint
          density="comfortable"
        />
      </div>

      <div class="settings-field">
        <label class="settings-field__label">{{
          $t("settings.reminder_lead_minutes")
        }}</label>
        <p class="settings-field__hint">
          {{ $t("settings.reminder_lead_minutes_hint") }}
        </p>
        <div class="reminder-lead-options">
          <VCheckbox
            v-for="option in reminderLeadOptions"
            :key="option.value"
            v-model="reminderLeadMinutes"
            :value="option.value"
            :label="option.title"
            density="comfortable"
            hide-details
          />
        </div>
      </div>
    </section>

    <!-- Confirmation email -->
    <section class="settings-card" v-if="isPublic">
      <div class="settings-card__header">
        <h3>{{ $t("settings.section_confirmation_email") }}</h3>
        <span class="settings-card__badge settings-card__badge--internal">
          {{ $t("settings.badge_internal") }}
        </span>
      </div>

      <VSelect
        v-model="defaultLocale"
        :items="[
          { title: 'English', value: 'en' },
          { title: 'Українська', value: 'uk' },
          { title: 'Čeština', value: 'cs' },
        ]"
        item-title="title"
        item-value="value"
        :label="$t('settings.confirmation_email_language')"
        density="comfortable"
      />

      <div class="settings-field">
        <label class="settings-field__label">
          {{ $t("settings.custom_confirmation_message") }}
        </label>
        <textarea
          v-model="customConfirmationMessage"
          :placeholder="$t('settings.custom_confirmation_message')"
        ></textarea>
      </div>
    </section>

    <div class="company-settings__savebar">
      <span
        class="company-settings__savebar-status"
        :class="{ 'is-dirty': isDirty }"
      >
        {{
          isDirty ? $t("settings.unsaved_changes") : $t("settings.all_saved")
        }}
      </span>

      <VBtn
        :loading="loading"
        class="company-settings__save"
        variant="text"
        @click="saveCompany"
      >
        {{ $t("save") }}

        <template #default v-if="success">
          <VIcon icon="mdi-check-circle-outline" color="white"></VIcon>
        </template>
      </VBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import PhotoUploader from "../photo-uploader.vue";
import DeleteDialog from "../delete-popup.vue";
import { useCompanyStore } from "@/main/stores/company.store";
import { updateCompany } from "@/main/services/api.service";
import { CurrentCompany } from "@/main/types/company.type";

const companyStore = useCompanyStore();

const possibleRating = [1, 2, 3, 4, 5];
const loading = ref(false);
const success = ref(false);
const copied = ref(false);
const colorMenuOpen = ref(false);
const images = ref<
  {
    id?: number;
    url?: File;
  }[]
>([]);
const deletedImageIds = ref<Set<number>>(new Set());
const deleteImageDialog = ref(false);
const imagePendingDeleteId = ref<number | null>(null);

const businessName = ref(companyStore.currentCompany?.title ?? "");
const description = ref(companyStore.currentCompany?.description ?? "");
const isPublic = ref(companyStore.currentCompany?.is_public ?? false);
const timezone = ref(companyStore.currentCompany?.timezone ?? "UTC");
const timezoneOptions = computed(() => {
  const zones =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : ["UTC"];
  const options = new Set(zones);
  if (timezone.value) options.add(timezone.value);
  return Array.from(options).sort();
});
const phone = ref(companyStore.currentCompany?.phone ?? "");
const email = ref(companyStore.currentCompany?.email ?? "");
const themeColor = ref(companyStore.currentCompany?.theme_color ?? "#1976D2");
const minAdvanceHours = ref(
  companyStore.currentCompany?.min_booking_advance_hours ?? 0,
);
const maxAdvanceDays = ref(
  companyStore.currentCompany?.max_booking_advance_days ?? 90,
);
const reminderLeadOptions = [
  { title: "1:30", value: 90 },
  { title: "1:00", value: 60 },
  { title: "0:30", value: 30 },
  { title: "0:15", value: 15 },
];
const reminderLeadMinutes = ref<number[]>(
  companyStore.currentCompany?.reminder_lead_minutes ?? [60],
);
const defaultLocale = ref(companyStore.currentCompany?.default_locale ?? "en");
const customConfirmationMessage = ref(
  companyStore.currentCompany?.custom_confirmation_message ?? "",
);
const logo = ref<File | undefined>(undefined);

const snapshot = () =>
  JSON.stringify({
    businessName: businessName.value,
    description: description.value,
    isPublic: isPublic.value,
    timezone: timezone.value,
    phone: phone.value,
    email: email.value,
    themeColor: themeColor.value,
    minAdvanceHours: minAdvanceHours.value,
    maxAdvanceDays: maxAdvanceDays.value,
    reminderLeadMinutes: [...reminderLeadMinutes.value].sort(),
    defaultLocale: defaultLocale.value,
    customConfirmationMessage: customConfirmationMessage.value,
  });

const savedSnapshot = ref(snapshot());

const isDirty = computed(
  () =>
    snapshot() !== savedSnapshot.value ||
    images.value.length > 0 ||
    !!logo.value,
);

const logoPreviewUrl = computed(() => {
  if (logo.value) return URL.createObjectURL(logo.value);
  return companyStore.currentCompany?.logo_url ?? undefined;
});

const onLogoChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) logo.value = file;
};

const visibleCompanyImages = computed(
  () =>
    companyStore.currentCompany?.images.filter(
      (image) => !deletedImageIds.value.has(image.id),
    ) ?? [],
);

const mainImage = computed(() => visibleCompanyImages.value[0] || null);

const onImagesUpdate = (e: { id?: number | undefined; url: File }) => {
  const idx = images.value.findIndex((img) => img.id && img.id === e.id);
  if (idx !== -1) {
    images.value.splice(idx, 1, e);
  } else {
    images.value.push(e);
  }
};

const onRemovePendingUpload = (e: { url: File; id?: number }) => {
  const idx = images.value.findIndex(
    (img) => img.url === e.url || (!!e.id && img.id === e.id),
  );
  if (idx !== -1) images.value.splice(idx, 1);
};

const onImageDelete = (e: { id: number }) => {
  imagePendingDeleteId.value = e.id;
  deleteImageDialog.value = true;
};

const confirmDeleteImage = () => {
  if (imagePendingDeleteId.value == null) return;
  deletedImageIds.value.add(imagePendingDeleteId.value);
  const idx = images.value.findIndex(
    (img) => img.id === imagePendingDeleteId.value,
  );
  if (idx !== -1) images.value.splice(idx, 1);
  images.value.push({ id: imagePendingDeleteId.value });
  imagePendingDeleteId.value = null;
};

const companyImagesLenght = computed(() => visibleCompanyImages.value.length);

const copyBookingUrl = async () => {
  const url = companyStore.currentCompany?.booking_url;
  if (!url) return;

  try {
    await navigator.clipboard.writeText(url);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    // clipboard access unavailable, ignore
  }
};

const saveCompany = async () => {
  loading.value = true;

  if (companyStore.currentCompany) {
    const company = await updateCompany(companyStore.currentCompany.id!, {
      title: businessName.value,
      description: description.value,
      images: images.value,
      is_public: isPublic.value,
      timezone: timezone.value,
      phone: phone.value,
      email: email.value,
      theme_color: themeColor.value,
      logo: logo.value,
      min_booking_advance_hours: minAdvanceHours.value,
      max_booking_advance_days: maxAdvanceDays.value,
      reminder_lead_minutes: reminderLeadMinutes.value,
      default_locale: defaultLocale.value,
      custom_confirmation_message: customConfirmationMessage.value,
    });
    images.value = [];
    deletedImageIds.value = new Set();
    logo.value = undefined;

    companyStore.currentCompany = null;

    companyStore.currentCompany = company as CurrentCompany;

    savedSnapshot.value = snapshot();
    success.value = true;
    setTimeout(() => {
      success.value = false;
    }, 1000);
  }

  loading.value = false;
};
</script>

<style scoped lang="scss">
@use "../../styles/_breakpoints" as bp;

.company-settings {
  flex: 1;
  width: 100%;
  max-width: 1100px;
  min-width: 0;
  margin: 0 auto;
  padding: 32px 24px 0;
  display: flex;
  flex-direction: column;
  row-gap: 20px;

  @include bp.tablet {
    padding: 20px 0;
  }

  // The layout's content column has a fixed height at this breakpoint
  // (see column-with-sidebar.vue) with no overflow of its own, same as
  // the sidebar nav list - so this panel scrolls internally too, and the
  // sticky savebar below sticks to the bottom of this scroll area.
  @media screen and (min-width: 960px) {
    height: 100%;
    overflow-y: auto;
  }
}

.settings-card {
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;

  @include bp.tablet {
    padding: 16px;
  }

  &__header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    column-gap: 12px;
    row-gap: 4px;

    h3 {
      min-width: 0;
      color: #403838;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }

  &__badge {
    flex: none;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 4px 10px;
    border-radius: 999px;
    white-space: nowrap;

    &--public {
      background: #e5f4ea;
      color: #1e7e34;
    }

    &--internal {
      background: #eef0f3;
      color: #696e7c;
    }
  }

  &__hint {
    margin: -8px 0 0;
    color: #696e7c;
    font-size: 12px;
    line-height: 16px;
  }
}

.settings-field {
  display: flex;
  flex-direction: column;
  row-gap: 8px;

  &__label {
    color: #403838;
    font-size: 13px;
    font-weight: 600;
  }

  textarea {
    display: block;
    padding: 12px 16px;
    color: #000;
    font-size: 13px;
    line-height: 18px;
    min-height: 100px;
    width: 100%;
    border-radius: 10px;
    border: 1px solid #d8dbe0;
    background: #fff;
    resize: vertical;

    &::placeholder {
      color: #9aa0a6;
    }

    &:focus {
      outline: none;
      border-color: #403838;
    }
  }
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  column-gap: 16px;
}

.reminder-lead-options {
  display: flex;
  flex-wrap: wrap;
  column-gap: 24px;
}

.business-name-field__input {
  width: 100%;
  color: #403838;
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
  padding-bottom: 6px;
  border: none;
  border-bottom: 1px solid #d8dbe0;
  background: transparent;

  &:focus {
    outline: none;
    border-bottom-color: #403838;
  }

  &::placeholder {
    color: #9aa0a6;
  }
}

.photos-grid {
  display: flex;
  flex-direction: column;
  row-gap: 12px;

  &__main {
    width: 100%;
    height: 220px;
  }

  &__thumbs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  &__thumb {
    width: 100%;
    height: 96px;
  }
}

.booking-link {
  display: flex;
  align-items: center;
  column-gap: 8px;
  background: #fff;
  border: 1px solid #eceef1;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;

  &__url {
    flex: 1;
    min-width: 0;
    color: #403838;
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }

  &__copy {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    color: #696e7c;
    padding: 2px;

    &:hover {
      color: #403838;
    }
  }
}

.color-swatch {
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d8dbe0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  width: fit-content;

  &:hover {
    border-color: #403838;
  }

  &__dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  &__hex {
    font-family: monospace;
    font-size: 13px;
    color: #403838;
    text-transform: uppercase;
  }
}

.logo-field {
  &__row {
    display: flex;
    align-items: center;
    column-gap: 16px;
  }

  &__preview {
    flex: none;
    width: 64px;
    height: 64px;
    border-radius: 12px;
    overflow: hidden;
    background: #e7eaee;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #9aa0a6;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &-empty {
      border: 1px dashed #c2c7cf;
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
  }

  &__button {
    color: #403838;
    font-size: 13px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
    width: fit-content;
  }

  &__hint {
    color: #696e7c;
    font-size: 12px;
  }

  &__input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
}

.company-settings__feedbacks {
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
  column-gap: 16px;
  align-items: center;
  color: #403838;
  font-size: 18px;
  line-height: 15px;

  span {
    color: #403838;
    font-size: 14px;
    text-decoration-line: underline;
    cursor: pointer;
    white-space: nowrap;
  }
}

.feedbacks {
  height: 27px;
  display: flex;
  gap: 8px;
  flex: none;
}

.company-settings__savebar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 12px;
  background: #fff;
  border-top: 1px solid #eceef1;
  padding: 16px 4px;
  margin-top: 4px;

  &-status {
    color: #2e7d32;
    font-size: 13px;

    &.is-dirty {
      color: #696e7c;
    }
  }
}

.company-settings__save {
  border-radius: 10px;
  background: #403838;
  color: #fff;
  padding: 16px;
  height: unset;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;

  i {
    font-size: 16px;
  }
}
</style>
